import { Injectable, NgZone, OnDestroy, inject, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';

export const INACTIVITY_LIMIT_MS = 20 * 60 * 1000;
// How often the wall-clock is compared against the last activity timestamp. The poll only decides
// *when to look*; the amount of elapsed time is always derived from real timestamps, so throttled
// or paused browser timers cannot cause a premature (or missed) logout.
export const INACTIVITY_CHECK_INTERVAL_MS = 30 * 1000;
// Activity events fire in bursts (e.g. mousemove); throttle timestamp writes to once per second.
const ACTIVITY_THROTTLE_MS = 1000;

const TRACKED_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
];

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly authService = inject(AuthService);
  private readonly tracking = signal(false);

  private lastActivityAt = Date.now();
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private loggingOut = false;

  start(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.tracking()) {
      this.markActivity();
      return;
    }

    this.tracking.set(true);
    this.loggingOut = false;
    this.markActivity(true);

    this.zone.runOutsideAngular(() => {
      TRACKED_EVENTS.forEach((eventName) =>
        window.addEventListener(eventName, this.handleUserInteraction, {
          capture: true,
          passive: true,
        })
      );
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.intervalHandle = setInterval(
        () => this.evaluateInactivity(),
        INACTIVITY_CHECK_INTERVAL_MS
      );
    });
  }

  stop(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.tracking()) {
      return;
    }

    this.tracking.set(false);
    this.zone.runOutsideAngular(() => {
      TRACKED_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, this.handleUserInteraction, true)
      );
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    });
    this.clearInterval();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private readonly handleUserInteraction = () => {
    this.markActivity();
  };

  // When the tab returns to the foreground (after being backgrounded, or after the machine woke
  // from sleep) elapsed real time is evaluated immediately rather than waiting for the next poll.
  private readonly handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.evaluateInactivity();
    }
  };

  private markActivity(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastActivityAt < ACTIVITY_THROTTLE_MS) {
      return;
    }
    this.lastActivityAt = now;
  }

  private evaluateInactivity(): void {
    if (!this.tracking() || this.loggingOut) {
      return;
    }

    const idleFor = Date.now() - this.lastActivityAt;
    if (idleFor < INACTIVITY_LIMIT_MS) {
      return;
    }

    this.loggingOut = true;
    this.zone.run(() => {
      this.stop();
      void this.authService.logoutDueToInactivity();
    });
  }

  private clearInterval(): void {
    if (this.intervalHandle === null) {
      return;
    }
    clearInterval(this.intervalHandle);
    this.intervalHandle = null;
  }
}
